import React, { useEffect } from "react";
import { Grid, Box, Typography, Radio, Button } from "@mui/material";
import { useTranslation } from "react-i18next";

import { OnboardingStage } from "../../../models/onboarding.stage";
import { UploadValueType } from "../../../util/enums";
import { UploadFile } from "../UploadCenter/UploadFile";
import { colors } from "@zapalacorp/tokens";
import { ExportCurve } from "iconsax-react";

type Props = {
  uploadType?: UploadValueType;
  setUploadType: (uploadType: UploadValueType) => void;
  onboardingStage?: OnboardingStage;
  onUpdateOnboardingStage?: (onboardingStage: OnboardingStage) => void;
};

const UploadCentre: React.FC<Props> = ({
  uploadType,
  setUploadType,
  onboardingStage,
  onUpdateOnboardingStage,
}) => {
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (
      onboardingStage &&
      !uploadType &&
      (onboardingStage?.accessWasUploaded ||
        onboardingStage?.fleetWasUploaded ||
        onboardingStage?.inventoryWasUploaded ||
        onboardingStage?.instructionWasUploaded ||
        onboardingStage?.materialWasUploaded ||
        onboardingStage?.staffWasUploaded ||
        onboardingStage?.serviceWasUploaded)
    ) {
      setUploadType(UploadValueType.UPLOAD_VALUE_OWN_LIST);
    }
  }, [onboardingStage, setUploadType]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploadType((event.target as HTMLInputElement).value as UploadValueType);
  };

  const handleDownloadTemplate = () => {
    const templateContent = `Item Name,Description,Category,Unit Price,Quantity
Sample Item 1,Description for item 1,Category A,10.00,100
Sample Item 2,Description for item 2,Category B,15.50,50
Sample Item 3,Description for item 3,Category A,8.75,200`;

    const blob = new Blob([templateContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "shipeezi_inventory_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box data-cy="uploadCenter" sx={{ width: "100%" }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <Box
            sx={{
              border:
                uploadType === UploadValueType.UPLOAD_VALUE_OWN_LIST
                  ? "2px solid #0055A4"
                  : "1px solid #E4E7EC",
              borderRadius: "8px",
              backgroundColor: "#FFFFFF",
              cursor: "pointer",
              transition: "all 0.2s ease",
              p: 3,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              "&:hover": {
                borderColor: colors.brandBlue500,
                boxShadow: "0 0 0 4px rgba(0, 85, 164, 0.08)",
              },
            }}
            onClick={() => setUploadType(UploadValueType.UPLOAD_VALUE_OWN_LIST)}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Radio
                checked={uploadType === UploadValueType.UPLOAD_VALUE_OWN_LIST}
                value={UploadValueType.UPLOAD_VALUE_OWN_LIST}
                onChange={handleChange}
                sx={{
                  color: "#D0D5DD",
                  "&.Mui-checked": { color: colors.brandBlue500 },
                  p: 0,
                  mt: 0.5,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 600,
                    fontSize: "18px",
                    lineHeight: "28px",
                    color: colors.brandBlue500,
                    mb: 0.5,
                  }}
                >
                  {translate("uploadOwnList", { defaultValue: "Upload my own list" })}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#475467",
                    mb: 2,
                  }}
                >
                  {translate("uploadOwnListDescription", {
                    defaultValue: "Bring in your own data to customise Shipeezi from the start.",
                  })}
                </Typography>

                <Button
                  variant="outlined"
                  startIcon={<ExportCurve size={20} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadTemplate();
                  }}
                  sx={{
                    borderColor: colors.brandBlue500,
                    color: colors.brandBlue500,
                    borderRadius: "8px",
                    px: 2.5,
                    py: 1,
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "rgba(0, 85, 164, 0.04)",
                      borderColor: colors.brandBlue500,
                    },
                  }}
                >
                  {translate("downloadCsvTemplate", {
                    defaultValue: "Download our CSV template here",
                  })}
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <Box
            sx={{
              border:
                uploadType === UploadValueType.UPLOAD_VALUE_SHIPEEZI_LIST
                  ? "2px solid #0055A4"
                  : "1px solid #E4E7EC",
              borderRadius: "8px",
              backgroundColor: "#FFFFFF",
              cursor: "pointer",
              transition: "all 0.2s ease",
              p: 3,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              "&:hover": {
                borderColor: colors.brandBlue500,
                boxShadow: "0 0 0 4px rgba(0, 85, 164, 0.08)",
              },
            }}
            onClick={() => setUploadType(UploadValueType.UPLOAD_VALUE_SHIPEEZI_LIST)}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Radio
                checked={uploadType === UploadValueType.UPLOAD_VALUE_SHIPEEZI_LIST}
                value={UploadValueType.UPLOAD_VALUE_SHIPEEZI_LIST}
                onChange={handleChange}
                sx={{
                  color: "#D0D5DD",
                  "&.Mui-checked": { color: colors.brandBlue500 },
                  p: 0,
                  mt: 0.5,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 600,
                    fontSize: "18px",
                    lineHeight: "28px",
                    color: colors.brandBlue500,
                    mb: 0.5,
                  }}
                >
                  {translate("useShipeeziSDefaultList", {
                    defaultValue: "Use Shipeezi's default list (ISO17451-1:2016)",
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#475467",
                  }}
                >
                  {translate("useShipeeziSDefaultListDescription", {
                    defaultValue:
                      "Start quickly with our pre-configured list, built on the global ISO17451-1:2016 standard. You can update or expand it anytime.",
                  })}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {!!uploadType && (
        <Box data-cy="uploadCenterOptions">
          {uploadType === UploadValueType.UPLOAD_VALUE_SHIPEEZI_LIST ? (
            <>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Manrope",
                  fontWeight: 600,
                  fontSize: "18px",
                  color: "#101828",
                  mb: 1,
                }}
              >
                {translate("inventoryListIso174511", {
                  defaultValue: "Inventory list ISO17451-1:2016 Certified covers",
                })}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Manrope",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "#475467",
                  mb: 3,
                }}
              >
                {translate("iso174511Description", {
                  defaultValue:
                    "Items, Rooms, Packaging type, Method of preparation, Mode type, Exception codes, Exception location code, Item instructions and destination.",
                })}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: colors.brandBlue500,
                          mt: 1,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Manrope",
                            fontSize: "16px",
                            lineHeight: "20px",
                            mb: 0.5,
                            color: colors.gray600,
                          }}
                        >
                          Equipment
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.gray600,
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          One default equipment will be created.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: colors.brandBlue500,
                          mt: 1,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Manrope",
                            fontSize: "16px",
                            lineHeight: "20px",
                            mb: 0.5,
                            color: colors.gray600,
                          }}
                        >
                          Services
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.gray600,
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          One default service type will be created.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: colors.brandBlue500,
                          mt: 1,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Manrope",
                            fontSize: "16px",
                            lineHeight: "20px",
                            mb: 0.5,
                            color: colors.gray600,
                          }}
                        >
                          Instructions
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.gray600,
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          Predefined instructions can be applied in Sales and Operations. for
                          uplift, delivery or move coordinator
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: colors.brandBlue500,
                          mt: 1,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Manrope",
                            fontSize: "16px",
                            lineHeight: "20px",
                            mb: 0.5,
                            color: colors.gray600,
                          }}
                        >
                          Checklist & Form
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.gray600,
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          Includes a standard risk assessment template.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: colors.brandBlue500,
                          mt: 1,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Manrope",
                            fontSize: "16px",
                            lineHeight: "20px",
                            mb: 0.5,
                            color: colors.gray600,
                          }}
                        >
                          List of Materials
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.gray600,
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          Common packing material types will be preloaded.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: colors.brandBlue500,
                          mt: 1,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Manrope",
                            fontSize: "16px",
                            lineHeight: "20px",
                            mb: 0.5,
                            color: colors.gray600,
                          }}
                        >
                          Staff member
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.gray600,
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          One default staff member will be created.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: colors.brandBlue500,
                          mt: 1,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Manrope",
                            fontSize: "16px",
                            lineHeight: "20px",
                            mb: 0.5,
                            color: colors.gray600,
                          }}
                        >
                          Access
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.gray600,
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          Default access levels and permissions will be created.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <UploadFile
                uploadType={uploadType}
                onboardingStage={onboardingStage}
                onUpdateOnboardingStage={onUpdateOnboardingStage}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default UploadCentre;
